import { ICellRendererParams } from "ag-grid-community";
import Metric, { NumericType, TimeType } from 'src/shared/models/metric';
import MetricData from 'src/shared/models/metric-data';

export enum EState {
    LoggedOut = 0,
    Available = 1,
    Inbound = 2,
    WorkingContacts = 3,
    Outbound = 4,
    Unavailable = 8,
    Custom = 16,
    Mixed = 32,
}

export enum ModuleView {
    Table = 'table',
    Chart = 'chart',
    Dual = 'dual'
}

export const FOOTER_ROW_DEFAULT_VALUE = "-";

export interface AgentContactActivity {
    AgentNo: number;
    SkillName: string;
    AgentContactStateDisplayName: string;
    ContactNumber: string;
    AgentContactTime: string;
    ChannelDisplayName: string;
}

export class ModuleData {
    RowValues: Array<LineContent> | Array<RowContent>;
    IncompatibleSettings: boolean;
    MetricsAgentStatesColumnValues;
    MetricsAgentStatesRowValues?: Array<RowContent>;
}

export class LineContent {
    Group: ModuleDataGroupItem;
    ParentGroupName: string;
    State: AgentState;
    Skill: Array<string>;
    Duration: string;
    StartTime: string;
    HighlightValue: boolean;
}

export class RowContent extends LineContent {
    metricTypes: NumericType[];
    metricTypeNames: string[];
    metricTimeTypes: TimeType[];
    metricData: Metric[];
    attributeIDs: number[];
    attributeValue: string[];
}

export class ModuleDataGroupItem { // Keep properties in sync in TypeScript: ClearView Source/src/shared/models/module-data-group-item.ts
    groupID: number;
    groupLevel: number;
    groupName: string;
    groupHierarchy: string;
}

export class SettingsDistinctStates {
    States: Array<DistinctAgentState>;
    AgentStateCategories: Map<AgentStateType, string>;
    ShowSourceRadio: boolean;
    DefaultEnabled: boolean;
}

export class DistinctAgentState {
    AgentStateName: string;
    OutStateDescription: string;
    IsACW: boolean;
}

export enum AgentStateType {
    Invalid = 0,
    Inbound = 1,
    Outbound = 2,
    Available = 3,
    Unavailable = 4,
    ACW = 5,
    LoggedIn = 6,
    LoggedOut = 7,
    Dialer = 8,
}

export const _stateColors = {
    green: "#278A33",
    orange: "#F05D21",
    purple: "#7D3BC8",
    red: "#CC0816",
    yellow: "#EFA716",
    gray: "#D3D6DA",
    blue: "#007CBE",
};

export class SingleAgentState {
    GroupID: number;
    GroupName: string;
    CurrentState: AgentState;
    Duration: string;
    StateStartTime: string; //was DateTime Class
    UserID?: string;
    AgentSessionID?: string;
}

// changed from struct
export class AgentState {
    State: EState; // used for mongo method
    AgentStateBucket: AgentStateBuckets; //used in QE pull
    DisplayState: string;
    Color: string;
    /// <summary>
    /// Current Agent States Responsive Dashboard module uses this boolean flag (marked by the Distinct State) to flag this AgentState as ACW.
    /// </summary>
    IsACW: boolean;
    IsDialer: boolean;
}

export enum AgentStateBuckets {
    LoggedOut = 0,
    Available = 1,
    AvailableAndDialer = 2,
    WorkingContacts = 3,
    WorkingContactsAndDialer = 4,
    Unavailable = 5
}

export class SingleAgentDetailsRSD {
    Details: Array<AgentStateDetailsRSD>;
    ImagePath: string;
    AgentStateCategories: Map<AgentStateType, string>;
    LastUpdatedDateTime: string;
}

export class AgentStateDetailsRSD {
    SkillNo: number;
    CampaignNo: number;
    AgentSessionID: string;
    AgentNo: number;
    TeamNo: number;
    UserId: string;
    EmpGroup1ID: number;
    DtAgentSessionStartTimestamp: string;
    AgentStateNo: number;
    Dialer: boolean;
    DtAgentSessionActivityStartTimestamp: string;
    ActivityStartTimeDisplay: string;
    AgentStateBucketID: string;
    AgentStateBucketName: string;
    DisplayStateName: string;
    UnavailableStateNo: number;
    UnavailableStateName: string;
    UnavailableSystem: boolean;
    UnavailableCustom: boolean;
}

export class SingleAgentDetails {
    //list was initialized before
    Details: Array<AgentStateDetails>;
    ImagePath: string;
    AgentStateCategories: Map<AgentStateType, string>;
    LastUpdatedDateTime: string;
}

export class AgentStateDetails {
    CompanyID: number;
    EmpGroup1I: number;
    AgentSessio: number;
    StateIndex: number;
    ReportingDa: string; //was DateTime Class
    StartDate: string; //was DateTime Class
    EndDate: string; //was DateTime Class
    Duration: number;
    AgentStateName: string;
    OutStateDescription: string;
    SkillID: number;
    SkillName: string;
    MediaType: string;
    LastUpdated: string; //was DateTime class
    IsACW: boolean;
    IsDialer: boolean;
    IsLoggedIn: boolean;
    IsLoggedOut: boolean;
    AgentStateType: AgentStateType;
    displayStatus: string;
    displayColor: string;
    startTimeDisplayValue: string | number; // converting from dateTime to mili for sorting
    durationDisplayValue: string;
    spansMultipleDays: boolean;
}

export class AgentFrontEnd {
    name: string;
    currentState: CurrentStateFrontEnd;
    duration: string;
    stateStartTime: string;
    profilePicturePath: string;
}

export class CurrentStateFrontEnd {
    displayState: string;
}

export interface StateSetting {
    Color: string;
    EState: EState;
    IsACW: boolean;
    StateDisplayName: string;
    enabled: boolean;
    max: number;
    min: number;
    rawMax: string;
    rawMin: string;
    version: number;
}

export interface AgentStateCellParams extends ICellRendererParams {
    getColorMap(): ColorInfo;
    isCXOne: boolean;
}

export interface ColorInfo {
    stateColorMap: Map<string, string>;
    _recentColors: string[];
}
